import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader, Button, Modal, LoadingSpinner, EmptyState } from '../components/ui';
import { QuestionnaireList } from '../components/surveys/QuestionnaireList';
import { QuestionnaireForm } from '../components/surveys/QuestionnaireForm';
import { QuestionManager } from '../components/surveys/QuestionManager';
import { ResponseList } from '../components/surveys/ResponseList';
import { surveyService } from '../services/survey.service';
import { messageHandler } from '../services/message-handler.service';
import type { SurveyQuestionnaire, QuestionnaireFormData } from '../types/survey';

function Surveys() {
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editQuestionnaire, setEditQuestionnaire] = useState<SurveyQuestionnaire | null>(null);
  const [manageQuestionnaire, setManageQuestionnaire] = useState<SurveyQuestionnaire | null>(null);
  const [responsesQuestionnaire, setResponsesQuestionnaire] = useState<SurveyQuestionnaire | null>(null);

  const { data: questionnaires = [], isLoading, error } = useQuery({
    queryKey: ['surveys'],
    queryFn: surveyService.list,
  });

  const createMutation = useMutation({
    mutationFn: (data: QuestionnaireFormData) => surveyService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      setIsCreateOpen(false);
      messageHandler.success('Questionário criado com sucesso');
    },
    onError: () => messageHandler.error('Erro ao criar questionário'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: QuestionnaireFormData }) =>
      surveyService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      setEditQuestionnaire(null);
      messageHandler.success('Questionário atualizado com sucesso');
    },
    onError: () => messageHandler.error('Erro ao atualizar questionário'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => surveyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      messageHandler.success('Questionário excluído com sucesso');
    },
    onError: () => messageHandler.error('Erro ao excluir questionário'),
  });

  const handleDelete = (questionnaire: SurveyQuestionnaire) => {
    messageHandler.confirm(
      'Excluir Questionário',
      `Tem certeza que deseja excluir o questionário "${questionnaire.title}"? Todas as perguntas e respostas serão removidas.`,
      () => deleteMutation.mutate(questionnaire.id)
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-red-600">Erro ao carregar questionários</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Questionários"
        description="Gerencie os questionários de satisfação e pesquisa"
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>Novo Questionário</Button>
        }
      />

      {questionnaires.length > 0 ? (
        <QuestionnaireList
          questionnaires={questionnaires}
          onEdit={setEditQuestionnaire}
          onDelete={handleDelete}
          onManage={setManageQuestionnaire}
          onResponses={setResponsesQuestionnaire}
        />
      ) : (
        <EmptyState
          title="Nenhum questionário encontrado"
          description="Crie seu primeiro questionário para começar"
          action={{ label: 'Novo Questionário', onClick: () => setIsCreateOpen(true) }}
        />
      )}

      {/* Create modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Novo Questionário">
        <QuestionnaireForm
          onSubmit={(data) => createMutation.mutate(data)}
          onCancel={() => setIsCreateOpen(false)}
          isLoading={createMutation.isPending}
        />
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={!!editQuestionnaire}
        onClose={() => setEditQuestionnaire(null)}
        title="Editar Questionário"
      >
        {editQuestionnaire && (
          <QuestionnaireForm
            questionnaire={editQuestionnaire}
            onSubmit={(data) => updateMutation.mutate({ id: editQuestionnaire.id, data })}
            onCancel={() => setEditQuestionnaire(null)}
            isLoading={updateMutation.isPending}
          />
        )}
      </Modal>

      {/* Manage questions modal */}
      <Modal
        isOpen={!!manageQuestionnaire}
        onClose={() => setManageQuestionnaire(null)}
        title={`Perguntas: ${manageQuestionnaire?.title}`}
      >
        {manageQuestionnaire && (
          <QuestionManager questionnaireId={manageQuestionnaire.id} />
        )}
      </Modal>

      {/* Responses modal */}
      <Modal
        isOpen={!!responsesQuestionnaire}
        onClose={() => setResponsesQuestionnaire(null)}
        title={`Respostas: ${responsesQuestionnaire?.title}`}
        size="2xl"
      >
        {responsesQuestionnaire && (
          <ResponseList questionnaireId={responsesQuestionnaire.id} />
        )}
      </Modal>
    </div>
  );
}

export default Surveys;
